import React, { useContext } from 'react';

import {
  renderGapSpacerAfterLastNodeIfNeeded,
  renderGapSpacerBeforeNodeIfNeeded,
} from './../../ricos-common-plugins/gap-spacer/gap-spacer-viewer.js';
import { ContentTraverserContext } from './../../ricos-context/index.js';
import { NodeTreeMapContext } from './../../ricos-context/viewer/NodeTreeMapContext.js';
import type { Node } from './../../ricos-schema/legacy/wix/rich_content/v1/index.js';
import type { RicosNodesViewRenderer, RicosNodesViewRendererProps } from './../../ricos-types/index.js';

import { getAnchor } from './components/Anchor.js';
import { nodeViewContainer as NodeViewContainer } from './rericos/renderers/plugins/nodeViewContainer.js';

const Fallback = ({ node }: { node: Node }) => {
  return process.env.NODE_ENV === 'development' ? (
    <div>{`Type ${node.type} is not supported yet (:`}</div>
  ) : null;
};

const RicosNodeDecorated = ({
  node,
  index,
  isRoot,
  shouldParagraphApplyTextStyle,
}: {
  node: Node;
  index: number;
  isRoot?: boolean;
  shouldParagraphApplyTextStyle?: boolean;
}) => {
  const { renderers, nodeViewDecorators } = useContext(ContentTraverserContext);
  const Renderer = renderers[node.type];
  if (!Renderer) {
    console.error(`RicosNodeDedcorated Error: no renderer passed for node type - ${node.type}`);
    return <Fallback node={node} />;
  }
  return nodeViewDecorators.apply(
    <Renderer
      node={node}
      key={node.id || index.toString()}
      RicosNodesRenderer={RicosNodesRenderer}
      isRoot={isRoot}
      nodeIndex={index}
      NodeViewContainer={NodeViewContainer}
      shouldParagraphApplyTextStyle={shouldParagraphApplyTextStyle}
    />,
    node,
    !!isRoot
  );
};

const RicosNodesRenderer: RicosNodesViewRenderer = ({
  nodes,
  isRoot,
  shouldParagraphApplyTextStyle,
  addAnchorsPrefix,
}: RicosNodesViewRendererProps) => {
  const { nodeTreeMap } = useContext(NodeTreeMapContext);

  return (
    <>
      {nodes.flatMap((node: Node, index: number) => {
        return [
          ...renderGapSpacerBeforeNodeIfNeeded(node, index, nodeTreeMap),
          <RicosNodeDecorated
            isRoot={isRoot}
            node={node}
            index={index}
            key={node.id || index.toString()}
            shouldParagraphApplyTextStyle={shouldParagraphApplyTextStyle}
          />,
          ...(addAnchorsPrefix ? [getAnchor(node, index, addAnchorsPrefix)] : []),
        ];
      })}
      {renderGapSpacerAfterLastNodeIfNeeded(nodes, nodeTreeMap)}
    </>
  );
};

export default RicosNodesRenderer;
